import { useEffect } from 'preact/hooks';
import { t } from '../i18n/index.js';
import { playSound } from '../audio/sounds.js';
import type { GameEndedView } from '../../shared/protocol.js';

export function gameOutcome(result: GameEndedView, userId: number): 'win' | 'loss' | 'none' {
  return result.winnerUserId === null ? 'none' : result.winnerUserId === userId ? 'win' : 'loss';
}

export function gameResultReason(result: GameEndedView, userId: number, maintenance?: boolean): string {
  if (result.reason === 'aborted' && maintenance) return t.closure.gameAborted;
  const outcome = gameOutcome(result, userId);
  // Snake (decision 2026-09-30): a crash or the time limit decided the game.
  if (result.reason === 'game_complete' && result.snakeEnd) return t.snake.end[result.snakeEnd](outcome);
  // Tetrax (2026-10-03): a stack over the top or the time limit.
  if (result.reason === 'game_complete' && result.tetraxEnd) return t.tetrax.end[result.tetraxEnd](outcome);
  // Chess (decision 2026-09-30): checkmate, stalemate, draw rules; a timeout can be a draw.
  if (result.reason === 'game_complete' && result.chessEnd) return t.chess.end[result.chessEnd](outcome);
  if (result.gameType === 'chess' && result.reason === 'turn_timeout' && outcome === 'none') return t.chess.end.timeout_insufficient();
  return t.result.reason[result.reason](outcome);
}

/**
 * Result of a two-player game. When the final position is shown behind the dialog
 * (onShowBoard), the dialog can be closed to look at it (user request 2026-09-29).
 */
export function GameResultDialog(props: {
  result: GameEndedView;
  userId: number;
  maintenance?: boolean;
  onClose: () => void;
  onShowBoard?: () => void;
}) {
  const { result } = props;
  const outcome = gameOutcome(result, props.userId);
  useEffect(() => {
    // The whole fleet sunk (user request 2026-09-29): a grand final blast after the last sinking,
    // then the result tune.
    const fleetSunk = result.gameType === 'battleship' && result.reason === 'all_ships_sunk';
    if (fleetSunk) playSound('fleetDestroyed', 900);
    const tuneAt = fleetSunk ? 5000 : 0;
    if (outcome === 'win') playSound('victory', tuneAt);
    else if (outcome === 'loss') playSound('defeat', tuneAt);
  }, [result.id]);
  return (
    <div class={`overlay ${props.onShowBoard ? 'overlay-light' : ''}`} role="presentation">
      <div class={`dialog card result result-${outcome}`} role="alertdialog" aria-modal="true">
        <div class="result-title">{t.result.title[outcome]}</div>
        <p>
          {t.games[result.gameType].name} · {t.game.opponent}: {result.opponent.displayName}
        </p>
        {result.score && (
          <p class="result-score">
            {(result.gameType === 'snake' ? t.snake.resultScore : result.gameType === 'tetrax' ? t.tetrax.resultScore : t.result.score)(result.score[0], result.score[1])}
          </p>
        )}
        <p class="muted">{gameResultReason(result, props.userId, props.maintenance)}</p>
        <div class="dialog-actions">
          {props.onShowBoard && (
            <button class="btn" onClick={props.onShowBoard}>
              {t.result.showBoard}
            </button>
          )}
          <button class="btn btn-primary" onClick={props.onClose} autofocus>
            {t.result.backToArena}
          </button>
        </div>
      </div>
    </div>
  );
}
